/**
 * ConfirmDialog Component - Integrated confirmation dialog (replaces window.confirm)
 */
import React from 'react';
import ReactDOM from 'react-dom';
import WarningIcon from '@mui/icons-material/Warning';

interface ConfirmDialogProps {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'warning' | 'danger' | 'info';
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  type = 'warning'
}) => {
  const getColor = () => {
    switch (type) {
      case 'danger':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'info':
      default:
        return '#2196f3';
    }
  };

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
        backdropFilter: 'blur(4px)',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: 'var(--node-body-background)',
          border: '1px solid var(--node-border-color)',
          borderRadius: '12px',
          padding: '24px',
          minWidth: '320px',
          maxWidth: '500px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {title && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px',
          }}>
            <WarningIcon style={{ color: getColor(), fontSize: '28px' }} />
            <h3 style={{
              margin: 0,
              color: 'var(--color-text-primary)',
              fontSize: '18px',
              fontWeight: 600,
            }}>
              {title}
            </h3>
          </div>
        )}

        {/* Message */}
        <p style={{
          margin: title ? '0 0 24px 0' : '0 0 24px 0',
          color: 'var(--color-text-primary)',
          fontSize: '14px',
          lineHeight: '1.5',
        }}>
          {message}
        </p>

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--node-body-background)',
              border: '1px solid var(--node-border-color)',
              borderRadius: '6px',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--node-header-background)';
              e.currentTarget.style.borderColor = 'var(--color-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--node-body-background)';
              e.currentTarget.style.borderColor = 'var(--node-border-color)';
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '8px 16px',
              backgroundColor: getColor(),
              border: `1px solid ${getColor()}`,
              borderRadius: '6px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ConfirmDialog service for imperative usage
interface ConfirmDialogData {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'warning' | 'danger' | 'info';
}

type ConfirmCallback = (confirmed: boolean) => void;

interface ConfirmRequest extends ConfirmDialogData {
  callback: ConfirmCallback;
}

type ConfirmListener = (request: ConfirmRequest) => void;

class ConfirmDialogService {
  private listeners: ConfirmListener[] = [];

  confirm(data: ConfirmDialogData): Promise<boolean> {
    return new Promise((resolve) => {
      const request: ConfirmRequest = {
        ...data,
        callback: resolve,
      };
      this.listeners.forEach(listener => listener(request));
    });
  }

  subscribe(listener: ConfirmListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }
}

export const confirmDialogService = new ConfirmDialogService();
export default ConfirmDialog;
